import { useEffect, useState } from "react";
import { Users, Clock, CheckCircle2, AlertTriangle, CalendarClock, ChevronDown, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { useLang } from "../i18n/LanguageContext";

const CHIPS = [
  { key: "checked_in", listKey: "checkedIn", color: "emerald", Icon: CheckCircle2 },
  { key: "not_logged", listKey: "notLogged", color: "slate", Icon: Clock },
  { key: "late", listKey: "late", color: "amber", Icon: AlertTriangle },
  { key: "leave", listKey: "leave", color: "violet", Icon: CalendarClock },
];
const COLOR = {
  emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
  slate: "border-slate-200 bg-slate-50 text-slate-700",
  amber: "border-amber-200 bg-amber-50 text-amber-700",
  violet: "border-violet-200 bg-violet-50 text-violet-700",
};
const ACTIVE = {
  emerald: "ring-2 ring-emerald-300",
  slate: "ring-2 ring-slate-300",
  amber: "ring-2 ring-amber-300",
  violet: "ring-2 ring-violet-300",
};

export const PortalTeamAttendance = () => {
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);

  useEffect(() => { api.portalTeamAttendance().then(setData).catch(() => setData(null)); }, []);

  if (!data) return null;
  if (data.total === 0) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="portal-team-attendance">
        <p className="text-sm text-slate-400 text-center">{t("portal.team.empty")}</p>
      </div>
    );
  }

  const list = open ? (data[open] || []) : [];
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="portal-team-attendance">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-heading font-bold text-slate-900 flex items-center gap-2"><Users className="h-5 w-5 text-blue-600" /> {t("portal.team.title")}</h2>
        <span className="text-xs text-slate-400">{data.branch_scope ? `${t("portal.team.scope")}: ${data.branch_scope}` : t("portal.team.all")}</span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {CHIPS.map(({ key, listKey, color, Icon }) => {
          const count = data.counts?.[key] || 0;
          const isOpen = open === key;
          return (
            <button key={key} type="button" data-testid={`team-chip-${key}`}
              onClick={() => setOpen(isOpen ? null : key)}
              className={`rounded-xl border p-3 text-right transition ${COLOR[color]} ${isOpen ? ACTIVE[color] : "hover:shadow-sm"}`}>
              <div className="flex items-center justify-between">
                <Icon className="h-4 w-4 opacity-70" />
                <span className="font-heading text-2xl font-bold font-mono-nums">{count}</span>
              </div>
              <p className="text-[11px] mt-1 opacity-80">{t(`portal.team.${listKey}`)}</p>
            </button>
          );
        })}
      </div>

      {open && (
        <div className="mt-3 rounded-xl border border-slate-100 divide-y divide-slate-50 max-h-56 overflow-y-auto" data-testid="team-list">
          {list.length === 0 ? (
            <p className="py-6 text-center text-sm text-slate-400">{t("portal.team.none")}</p>
          ) : list.map((r) => (
            <div key={r.employee_id} className="flex items-center justify-between px-3 py-2 text-sm">
              <span className="font-semibold text-slate-700 truncate">{r.name}{r.branch ? <span className="text-[11px] text-slate-400"> · {r.branch}</span> : null}</span>
              <span className="font-mono-nums text-xs text-slate-500 shrink-0">
                {r.check_in ? r.check_in : "—"}{r.check_out ? ` → ${r.check_out}` : ""}
              </span>
            </div>
          ))}
        </div>
      )}
      {open === "late" && (
        <p className="mt-2 text-[10px] text-slate-400 text-center">{t("portal.team.lateAfter")} {data.late_after}</p>
      )}
    </div>
  );
};
